import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from 'recharts';
import { ChartContainer, ChartTooltip, ChartTooltipContent, type ChartConfig } from '@/components/ui/chart';
import { formatVNDShort } from '@/lib/format';

export interface TrendPoint {
  month: string;
  units_sold: number;
  revenue_vnd: number;
  margin_vnd: number;
  margin_pct: number;
}

const CURRENT_COLOR = 'hsl(221 83% 53%)'; // primary blue
const MARGIN_COLOR = 'hsl(160 84% 39%)';  // emerald
const COMPARE_COLOR = 'hsl(220 9% 46%)';  // gray

const CHART_CONFIG = {
  revenue_vnd: { label: 'Doanh thu' },
  margin_vnd: { label: 'Lợi nhuận' },
  current: { label: 'Doanh thu kỳ hiện tại' },
  previous: { label: 'Doanh thu kỳ so sánh' },
} satisfies ChartConfig;

function formatMonthLabel(monthStr: string): string {
  const [year, month] = monthStr.split('-');
  return `T${parseInt(month!, 10)}/${year!.slice(2)}`;
}

const SoloTooltipFormatter = (value: unknown, name: string) => {
  const num = typeof value === 'number' ? value : Number(value);
  const labelMap: Record<string, string> = {
    revenue_vnd: 'Doanh thu',
    margin_vnd: 'Lợi nhuận',
  };
  const colorMap: Record<string, string> = {
    revenue_vnd: CURRENT_COLOR,
    margin_vnd: MARGIN_COLOR,
  };
  return (
    <div className="flex w-full items-center justify-between gap-3">
      <div className="flex items-center gap-2">
        <div className="h-2.5 w-2.5 rounded-[2px]" style={{ background: colorMap[name] }} />
        <span className="text-muted-foreground">{labelMap[name] ?? name}</span>
      </div>
      <span className="font-medium tabular-nums text-foreground">{formatVNDShort(num)}</span>
    </div>
  );
};

export interface ReportTrendChartProps {
  data: TrendPoint[];
  /** When provided, render compare overlay (focus on revenue) instead of revenue+margin */
  compareData?: TrendPoint[];
  currentLabel?: string;
  compareLabel?: string;
}

export function ReportTrendChart({
  data,
  compareData,
  currentLabel = 'Hiện tại',
  compareLabel = 'So sánh',
}: ReportTrendChartProps) {
  if (!data.length) {
    return (
      <div className="rounded-lg border border-dashed bg-muted/30 p-8 text-center text-sm text-muted-foreground">
        Chưa có dữ liệu sales theo tháng.
      </div>
    );
  }

  // Compare mode — overlay 2 revenue lines aligned by index
  if (compareData && compareData.length > 0) {
    const maxLen = Math.max(data.length, compareData.length);
    const merged = Array.from({ length: maxLen }).map((_, i) => ({
      index: `${i + 1}`,
      currentMonth: data[i]?.month,
      compareMonth: compareData[i]?.month,
      current: data[i]?.revenue_vnd ?? null,
      previous: compareData[i]?.revenue_vnd ?? null,
    }));

    const overlayFormatter = (value: unknown, name: string, _item: { color?: string }, _idx: number, payload: unknown) => {
      const num = typeof value === 'number' ? value : Number(value);
      const p = payload as { currentMonth?: string; compareMonth?: string };
      const labelMap: Record<string, { label: string; color: string; month?: string }> = {
        current: {
          label: currentLabel,
          color: CURRENT_COLOR,
          month: p.currentMonth ? formatMonthLabel(p.currentMonth) : undefined,
        },
        previous: {
          label: compareLabel,
          color: COMPARE_COLOR,
          month: p.compareMonth ? formatMonthLabel(p.compareMonth) : undefined,
        },
      };
      const meta = labelMap[name];
      return (
        <div className="flex w-full items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className="h-2.5 w-2.5 rounded-[2px]" style={{ background: meta?.color }} />
            <span className="text-muted-foreground">
              {meta?.label}
              {meta?.month && <span className="ml-1 text-[10px]">({meta.month})</span>}
            </span>
          </div>
          <span className="font-medium tabular-nums text-foreground">
            {value == null ? '—' : formatVNDShort(num)}
          </span>
        </div>
      );
    };

    return (
      <ChartContainer config={CHART_CONFIG} className="h-[260px] w-full">
        <AreaChart data={merged} margin={{ left: 4, right: 12, top: 8, bottom: 4 }}>
          <defs>
            <linearGradient id="currentFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={CURRENT_COLOR} stopOpacity={0.3} />
              <stop offset="95%" stopColor={CURRENT_COLOR} stopOpacity={0} />
            </linearGradient>
            <linearGradient id="compareFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={COMPARE_COLOR} stopOpacity={0.18} />
              <stop offset="95%" stopColor={COMPARE_COLOR} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid vertical={false} strokeDasharray="3 3" />
          <XAxis dataKey="index" tickLine={false} axisLine={false} tickMargin={8}
            tickFormatter={(v) => `Tháng ${v}`} />
          <YAxis tickLine={false} axisLine={false} tickMargin={4} width={48}
            tickFormatter={(v) => formatVNDShort(v)} />
          <ChartTooltip
            cursor={{ stroke: 'hsl(220 9% 46% / 0.4)', strokeDasharray: '3 3' }}
            content={
              <ChartTooltipContent
                formatter={overlayFormatter}
                labelFormatter={(label) => `Vị trí ${label}`}
              />
            }
          />
          {/* Compare line first (drawn under current) */}
          <Area
            type="monotone"
            dataKey="previous"
            stroke={COMPARE_COLOR}
            strokeWidth={2}
            strokeDasharray="4 4"
            fill="url(#compareFill)"
            dot={{ r: 3, fill: COMPARE_COLOR, strokeWidth: 0 }}
            activeDot={{ r: 5 }}
            connectNulls
          />
          <Area
            type="monotone"
            dataKey="current"
            stroke={CURRENT_COLOR}
            strokeWidth={2.5}
            fill="url(#currentFill)"
            dot={{ r: 3.5, fill: CURRENT_COLOR, strokeWidth: 0 }}
            activeDot={{ r: 6 }}
            connectNulls
          />
        </AreaChart>
      </ChartContainer>
    );
  }

  // Default mode — revenue + margin
  const chartData = data.map((d) => ({
    ...d,
    monthLabel: formatMonthLabel(d.month),
  }));

  return (
    <ChartContainer config={CHART_CONFIG} className="h-[260px] w-full">
      <AreaChart data={chartData} margin={{ left: 4, right: 12, top: 8, bottom: 4 }}>
        <defs>
          <linearGradient id="revenueFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={CURRENT_COLOR} stopOpacity={0.32} />
            <stop offset="95%" stopColor={CURRENT_COLOR} stopOpacity={0} />
          </linearGradient>
          <linearGradient id="marginFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={MARGIN_COLOR} stopOpacity={0.28} />
            <stop offset="95%" stopColor={MARGIN_COLOR} stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid vertical={false} strokeDasharray="3 3" />
        <XAxis dataKey="monthLabel" tickLine={false} axisLine={false} tickMargin={8} />
        <YAxis tickLine={false} axisLine={false} tickMargin={4} width={48}
          tickFormatter={(v) => formatVNDShort(v)} />
        <ChartTooltip
          cursor={{ stroke: 'hsl(220 9% 46% / 0.4)', strokeDasharray: '3 3' }}
          content={
            <ChartTooltipContent
              formatter={(value, name) => SoloTooltipFormatter(value, name as string)}
              labelFormatter={(label) => label as string}
            />
          }
        />
        <Area
          type="monotone"
          dataKey="revenue_vnd"
          stroke={CURRENT_COLOR}
          strokeWidth={2}
          fill="url(#revenueFill)"
          dot={{ r: 3, fill: CURRENT_COLOR, strokeWidth: 0 }}
          activeDot={{ r: 5 }}
        />
        <Area
          type="monotone"
          dataKey="margin_vnd"
          stroke={MARGIN_COLOR}
          strokeWidth={2}
          fill="url(#marginFill)"
          dot={{ r: 3, fill: MARGIN_COLOR, strokeWidth: 0 }}
          activeDot={{ r: 5 }}
        />
      </AreaChart>
    </ChartContainer>
  );
}
